import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X, Send, Lock, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { collaboratorAuthHeader } from "./useCollaborator";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/**
 * Final-selection submit modal (Phase G4).
 *
 * On open: pulls /selection/preview to render live math.
 *   - No-overage path → simple confirmation card.
 *   - Overage path    → adds line-item breakdown + required agreement checkbox.
 *
 * Submission posts to /selection/submit with `{ message, overage_agreed }`.
 * Server is the source of truth — we only show what /preview returned, so the
 * frontend can't accidentally hide a charge by mis-counting.
 */
export default function SelectionSubmitModal({ slug, token, open, onClose, onSubmitted }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const headers = collaboratorAuthHeader(token);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setAgreed(false);
    setMessage("");
    (async () => {
      try {
        const { data } = await axios.get(`${API}/galleries/${slug}/selection/preview`, { headers });
        setPreview(data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't load selection");
        onClose();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, slug, token]);

  const overage = preview?.requires_overage_agreement;
  const canSubmit = preview && (!overage || agreed) && !submitting;

  async function submit() {
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/galleries/${slug}/selection/submit`,
        { message: message.trim(), overage_agreed: agreed || !overage },
        { headers },
      );
      onSubmitted(data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        toast.error("Selection was already submitted — please refresh.");
        onClose();
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't submit");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-[90] bg-stone-900/55 backdrop-blur-sm"
            data-testid="selection-submit-backdrop"
          />
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[91] flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="relative w-full max-w-md bg-[#FAF9F6] shadow-2xl pointer-events-auto" data-testid="selection-submit-modal">
              <button onClick={onClose} className="absolute right-3 top-3 p-1 text-stone-500 hover:text-stone-900" aria-label="Close" data-testid="selection-submit-close">
                <X className="w-4 h-4" />
              </button>

              <div className="p-7">
                <div className="text-[10px] tracking-[0.32em] uppercase text-stone-500 mb-3">Final selection</div>
                <h2 className="font-serif text-3xl font-light tracking-tight text-stone-900 leading-tight">
                  Send selection to<br />the photographer
                </h2>

                {loading || !preview ? (
                  <div className="py-10 text-center text-xs uppercase tracking-[0.22em] text-stone-400">Loading…</div>
                ) : (
                  <>
                    <div className="mt-6 border border-stone-200 p-4 bg-white space-y-1.5 text-sm">
                      <div className="flex items-baseline justify-between">
                        <span className="text-stone-500">Photos selected</span>
                        <span className="font-medium text-stone-900" data-testid="preview-photo-count">{preview.photo_count}</span>
                      </div>
                      {preview.included_edits > 0 && (
                        <div className="flex items-baseline justify-between">
                          <span className="text-stone-500">Included edits</span>
                          <span className="text-stone-900">{preview.included_edits}</span>
                        </div>
                      )}
                      {overage && (
                        <>
                          <div className="border-t border-stone-200 my-2" />
                          <div className="flex items-baseline justify-between">
                            <span className="text-stone-500">Extra edits</span>
                            <span className="text-stone-900" data-testid="preview-extra-count">
                              {preview.extra_edit_count} × ${preview.extra_edit_price.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex items-baseline justify-between">
                            <span className="text-stone-700 font-medium">Overage total</span>
                            <span className="text-[#C9A961] font-medium" data-testid="preview-extra-total">${preview.extra_edit_total.toFixed(2)}</span>
                          </div>
                        </>
                      )}
                    </div>

                    {overage && (
                      <label className="mt-4 flex gap-2.5 items-start cursor-pointer" data-testid="overage-agreement-label">
                        <input
                          type="checkbox"
                          checked={agreed}
                          onChange={(e) => setAgreed(e.target.checked)}
                          className="mt-0.5 w-4 h-4 accent-stone-900"
                          data-testid="overage-agreement-checkbox"
                        />
                        <span className="text-xs text-stone-700 leading-relaxed">
                          I agree to pay <strong className="text-stone-900">${preview.extra_edit_total.toFixed(2)}</strong> for {preview.extra_edit_count} additional edit{preview.extra_edit_count === 1 ? "" : "s"}. The studio will issue an invoice.
                        </span>
                      </label>
                    )}

                    <label className="block mt-5">
                      <span className="text-[10px] tracking-[0.28em] uppercase text-stone-500">Note to the photographer <span className="opacity-60 normal-case tracking-normal">— optional</span></span>
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={3}
                        maxLength={500}
                        placeholder="Anything you'd like them to know about the selection…"
                        className="mt-1.5 w-full bg-white border border-stone-300 px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-stone-600 resize-none"
                        data-testid="selection-message"
                      />
                    </label>

                    <button
                      onClick={submit}
                      disabled={!canSubmit}
                      className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 h-12 bg-stone-900 text-[#F5EFE0] text-xs uppercase tracking-[0.28em] disabled:opacity-40 hover:opacity-90 transition-opacity"
                      data-testid="selection-submit-confirm"
                    >
                      <Send className="w-3.5 h-3.5" /> Send selection
                    </button>

                    {preview.photo_count === 0 && (
                      <p className="mt-3 inline-flex items-center gap-1 text-[11px] text-amber-700"><AlertCircle className="w-3 h-3" /> No favorites yet — pick at least one photo first.</p>
                    )}
                    {overage && !agreed && (
                      <p className="mt-2 inline-flex items-center gap-1 text-[10px] text-stone-500"><Lock className="w-3 h-3" /> Please agree to the overage to continue.</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
